const truthy = new Set(["1", "true", "yes", "on"]);

function asBool(value: unknown, fallback: boolean): boolean {
  if (typeof value !== "string") return fallback;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return fallback;
  return truthy.has(normalized);
}

function readLocalStorageFlag(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage?.getItem(key);
  } catch {
    return null;
  }
}

function asInt(value: unknown, fallback: number): number {
  if (typeof value !== "string") return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const DATA_CACHE_V2_ENABLED = asBool(import.meta.env.VITE_DATA_CACHE_V2, true);

export type QueryMode = "compat" | "strict";

const rawMode = String(import.meta.env.VITE_QUERY_MODE ?? "compat").trim().toLowerCase();
export const QUERY_MODE: QueryMode = rawMode === "strict" ? "strict" : "compat";

export const SUPABASE_SYNC_DEBUG = asBool(
  readLocalStorageFlag("debug:supabase-sync") ?? import.meta.env.VITE_DEBUG_SUPABASE_SYNC,
  false,
);

export const REALTIME_ROW_PATCHING_ENABLED = asBool(
  readLocalStorageFlag("feature:realtime-row-patching") ?? import.meta.env.VITE_REALTIME_ROW_PATCHING,
  true,
);

export const ORG_SYNC_ROUTER_OBSERVE_ENABLED = asBool(
  readLocalStorageFlag("feature:org-sync-router-observe") ?? import.meta.env.VITE_ORG_SYNC_ROUTER_OBSERVE,
  false,
);

export const VISIBLE_SYNC_POLL_MS = asInt(
  readLocalStorageFlag("tuning:visible-sync-poll-ms") ?? import.meta.env.VITE_VISIBLE_SYNC_POLL_MS,
  5 * 60_000,
);

export const RECONCILIATION_POLL_MS = asInt(
  readLocalStorageFlag("tuning:reconciliation-poll-ms") ?? import.meta.env.VITE_RECONCILIATION_POLL_MS,
  2 * 60_000,
);

export const RECONCILIATION_INITIAL_POLL_DELAY_MS = asInt(
  readLocalStorageFlag("tuning:reconciliation-initial-poll-delay-ms") ??
    import.meta.env.VITE_RECONCILIATION_INITIAL_POLL_DELAY_MS,
  5 * 60_000,
);

export const APPDATA_INITIAL_FULL_REFRESH_ENABLED = asBool(
  readLocalStorageFlag("feature:appdata-initial-full-refresh") ?? import.meta.env.VITE_APPDATA_INITIAL_FULL_REFRESH,
  false,
);

export const APPOINTMENTS_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:appointments-server-paging") ?? import.meta.env.VITE_APPOINTMENTS_SERVER_PAGING,
  true,
);

export const APPDATA_APPOINTMENTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:appointments-global-opportunity-hydration") ??
    import.meta.env.VITE_APPOINTMENTS_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPOINTMENTS_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:appointments-defer-heavy-providers") ??
    import.meta.env.VITE_APPOINTMENTS_DEFER_HEAVY_PROVIDERS,
  true,
);

export const PROJECTS_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:projects-server-paging") ?? import.meta.env.VITE_PROJECTS_SERVER_PAGING,
  true,
);

export const WAREHOUSE_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:warehouse-server-paging") ?? import.meta.env.VITE_WAREHOUSE_SERVER_PAGING,
  true,
);

export const ORDERING_QUEUE_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:ordering-queue-server-paging") ??
    import.meta.env.VITE_ORDERING_QUEUE_SERVER_PAGING,
  true,
);

export const PIPELINE_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:pipeline-server-paging") ?? import.meta.env.VITE_PIPELINE_SERVER_PAGING,
  true,
);

export const GROWTH_CHANNELS_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:growth-channels-server-paging") ??
    import.meta.env.VITE_GROWTH_CHANNELS_SERVER_PAGING,
  true,
);

export const INTAKE_REVIEW_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:intake-review-server-paging") ??
    import.meta.env.VITE_INTAKE_REVIEW_SERVER_PAGING,
  true,
);

export const PERMITTING_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:permitting-server-paging") ??
    import.meta.env.VITE_PERMITTING_SERVER_PAGING,
  true,
);

export const INSTALLER_OFFERS_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:installer-offers-server-paging") ??
    import.meta.env.VITE_INSTALLER_OFFERS_SERVER_PAGING,
  true,
);

export const SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:scheduling-dispatch-server-paging") ??
    import.meta.env.VITE_SCHEDULING_DISPATCH_SERVER_PAGING,
  true,
);

export const EXECUTION_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:execution-server-paging") ??
    import.meta.env.VITE_EXECUTION_SERVER_PAGING,
  true,
);

export const RESOLVE_SERVICE_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:resolve-service-server-paging") ??
    import.meta.env.VITE_RESOLVE_SERVICE_SERVER_PAGING,
  true,
);

export const RESOLVE_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:resolve-server-paging") ??
    import.meta.env.VITE_RESOLVE_SERVER_PAGING,
  true,
);

export const CONTRACTORS_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:contractors-server-paging") ??
    import.meta.env.VITE_CONTRACTORS_SERVER_PAGING,
  true,
);

export const CONTRACTOR_ONBOARDING_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:contractor-onboarding-server-paging") ??
    import.meta.env.VITE_CONTRACTOR_ONBOARDING_SERVER_PAGING,
  true,
);

export const CUSTOMERS_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:customers-server-paging") ??
    import.meta.env.VITE_CUSTOMERS_SERVER_PAGING,
  true,
);

export const COMMAND_DASHBOARD_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:command-dashboard-server-paging") ??
    import.meta.env.VITE_COMMAND_DASHBOARD_SERVER_PAGING,
  true,
);

export const REPORTS_SERVER_PAGING_ENABLED = asBool(
  readLocalStorageFlag("feature:reports-server-paging") ??
    import.meta.env.VITE_REPORTS_SERVER_PAGING,
  true,
);

export const JOB_DETAIL_SERVER_QUERY_ENABLED = asBool(
  readLocalStorageFlag("feature:job-detail-server-query") ??
    import.meta.env.VITE_JOB_DETAIL_SERVER_QUERY,
  true,
);

export const PROJECTS_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:projects-defer-heavy-providers") ??
    import.meta.env.VITE_PROJECTS_DEFER_HEAVY_PROVIDERS,
  true,
);

export const WAREHOUSE_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:warehouse-defer-heavy-providers") ??
    import.meta.env.VITE_WAREHOUSE_DEFER_HEAVY_PROVIDERS,
  true,
);

export const ORDERING_QUEUE_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:ordering-queue-defer-heavy-providers") ??
    import.meta.env.VITE_ORDERING_QUEUE_DEFER_HEAVY_PROVIDERS,
  true,
);

export const PIPELINE_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:pipeline-defer-heavy-providers") ??
    import.meta.env.VITE_PIPELINE_DEFER_HEAVY_PROVIDERS,
  true,
);

export const GROWTH_CHANNELS_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:growth-channels-defer-heavy-providers") ??
    import.meta.env.VITE_GROWTH_CHANNELS_DEFER_HEAVY_PROVIDERS,
  true,
);

export const INTAKE_REVIEW_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:intake-review-defer-heavy-providers") ??
    import.meta.env.VITE_INTAKE_REVIEW_DEFER_HEAVY_PROVIDERS,
  true,
);

export const PERMITTING_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:permitting-defer-heavy-providers") ??
    import.meta.env.VITE_PERMITTING_DEFER_HEAVY_PROVIDERS,
  true,
);

export const INSTALLER_OFFERS_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:installer-offers-defer-heavy-providers") ??
    import.meta.env.VITE_INSTALLER_OFFERS_DEFER_HEAVY_PROVIDERS,
  true,
);

export const SCHEDULING_DISPATCH_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:scheduling-dispatch-defer-heavy-providers") ??
    import.meta.env.VITE_SCHEDULING_DISPATCH_DEFER_HEAVY_PROVIDERS,
  true,
);

export const EXECUTION_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:execution-defer-heavy-providers") ??
    import.meta.env.VITE_EXECUTION_DEFER_HEAVY_PROVIDERS,
  true,
);

export const RESOLVE_SERVICE_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:resolve-service-defer-heavy-providers") ??
    import.meta.env.VITE_RESOLVE_SERVICE_DEFER_HEAVY_PROVIDERS,
  true,
);

export const RESOLVE_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:resolve-defer-heavy-providers") ??
    import.meta.env.VITE_RESOLVE_DEFER_HEAVY_PROVIDERS,
  true,
);

export const CONTRACTORS_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:contractors-defer-heavy-providers") ??
    import.meta.env.VITE_CONTRACTORS_DEFER_HEAVY_PROVIDERS,
  true,
);

export const CONTRACTOR_ONBOARDING_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:contractor-onboarding-defer-heavy-providers") ??
    import.meta.env.VITE_CONTRACTOR_ONBOARDING_DEFER_HEAVY_PROVIDERS,
  true,
);

export const CUSTOMERS_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:customers-defer-heavy-providers") ??
    import.meta.env.VITE_CUSTOMERS_DEFER_HEAVY_PROVIDERS,
  true,
);

export const COMMAND_DASHBOARD_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:command-dashboard-defer-heavy-providers") ??
    import.meta.env.VITE_COMMAND_DASHBOARD_DEFER_HEAVY_PROVIDERS,
  true,
);

export const REPORTS_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:reports-defer-heavy-providers") ??
    import.meta.env.VITE_REPORTS_DEFER_HEAVY_PROVIDERS,
  true,
);

export const JOB_DETAIL_DEFER_HEAVY_PROVIDERS_ENABLED = asBool(
  readLocalStorageFlag("feature:job-detail-defer-heavy-providers") ??
    import.meta.env.VITE_JOB_DETAIL_DEFER_HEAVY_PROVIDERS,
  true,
);

export const APPDATA_PROJECTS_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:projects-global-jobs-hydration") ??
    import.meta.env.VITE_PROJECTS_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_WAREHOUSE_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:warehouse-global-jobs-hydration") ??
    import.meta.env.VITE_WAREHOUSE_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_ORDERING_QUEUE_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:ordering-queue-global-jobs-hydration") ??
    import.meta.env.VITE_ORDERING_QUEUE_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_ORDERING_QUEUE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:ordering-queue-global-opportunity-hydration") ??
    import.meta.env.VITE_ORDERING_QUEUE_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_PIPELINE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:pipeline-global-opportunity-hydration") ??
    import.meta.env.VITE_PIPELINE_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_GROWTH_CHANNELS_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:growth-channels-global-jobs-hydration") ??
    import.meta.env.VITE_GROWTH_CHANNELS_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_GROWTH_CHANNELS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:growth-channels-global-opportunity-hydration") ??
    import.meta.env.VITE_GROWTH_CHANNELS_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_INTAKE_REVIEW_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:intake-review-global-jobs-hydration") ??
    import.meta.env.VITE_INTAKE_REVIEW_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_INTAKE_REVIEW_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:intake-review-global-opportunity-hydration") ??
    import.meta.env.VITE_INTAKE_REVIEW_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_PERMITTING_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:permitting-global-jobs-hydration") ??
    import.meta.env.VITE_PERMITTING_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_PERMITTING_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:permitting-global-opportunity-hydration") ??
    import.meta.env.VITE_PERMITTING_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_INSTALLER_OFFERS_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:installer-offers-global-jobs-hydration") ??
    import.meta.env.VITE_INSTALLER_OFFERS_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_INSTALLER_OFFERS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:installer-offers-global-opportunity-hydration") ??
    import.meta.env.VITE_INSTALLER_OFFERS_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_SCHEDULING_DISPATCH_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:scheduling-dispatch-global-jobs-hydration") ??
    import.meta.env.VITE_SCHEDULING_DISPATCH_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_SCHEDULING_DISPATCH_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:scheduling-dispatch-global-opportunity-hydration") ??
    import.meta.env.VITE_SCHEDULING_DISPATCH_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_EXECUTION_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:execution-global-jobs-hydration") ??
    import.meta.env.VITE_EXECUTION_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_EXECUTION_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:execution-global-opportunity-hydration") ??
    import.meta.env.VITE_EXECUTION_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_RESOLVE_SERVICE_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:resolve-service-global-jobs-hydration") ??
    import.meta.env.VITE_RESOLVE_SERVICE_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_RESOLVE_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:resolve-global-jobs-hydration") ??
    import.meta.env.VITE_RESOLVE_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_RESOLVE_SERVICE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:resolve-service-global-opportunity-hydration") ??
    import.meta.env.VITE_RESOLVE_SERVICE_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_RESOLVE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:resolve-global-opportunity-hydration") ??
    import.meta.env.VITE_RESOLVE_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_CONTRACTORS_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:contractors-global-jobs-hydration") ??
    import.meta.env.VITE_CONTRACTORS_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_CONTRACTORS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:contractors-global-opportunity-hydration") ??
    import.meta.env.VITE_CONTRACTORS_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_CONTRACTOR_ONBOARDING_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:contractor-onboarding-global-jobs-hydration") ??
    import.meta.env.VITE_CONTRACTOR_ONBOARDING_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_CONTRACTOR_ONBOARDING_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:contractor-onboarding-global-opportunity-hydration") ??
    import.meta.env.VITE_CONTRACTOR_ONBOARDING_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_CUSTOMERS_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:customers-global-jobs-hydration") ??
    import.meta.env.VITE_CUSTOMERS_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_CUSTOMERS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:customers-global-opportunity-hydration") ??
    import.meta.env.VITE_CUSTOMERS_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_COMMAND_DASHBOARD_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:command-dashboard-global-jobs-hydration") ??
    import.meta.env.VITE_COMMAND_DASHBOARD_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_COMMAND_DASHBOARD_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:command-dashboard-global-opportunity-hydration") ??
    import.meta.env.VITE_COMMAND_DASHBOARD_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_REPORTS_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:reports-global-jobs-hydration") ??
    import.meta.env.VITE_REPORTS_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_REPORTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:reports-global-opportunity-hydration") ??
    import.meta.env.VITE_REPORTS_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_JOB_DETAIL_GLOBAL_JOBS_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:job-detail-global-jobs-hydration") ??
    import.meta.env.VITE_JOB_DETAIL_GLOBAL_JOBS_HYDRATION,
  false,
);

export const APPDATA_JOB_DETAIL_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:job-detail-global-opportunity-hydration") ??
    import.meta.env.VITE_JOB_DETAIL_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_PROJECTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED = asBool(
  readLocalStorageFlag("feature:projects-global-opportunity-hydration") ??
    import.meta.env.VITE_PROJECTS_GLOBAL_OPPORTUNITY_HYDRATION,
  false,
);

export const APPDATA_SPEC_BACKFILL_ON_LOAD_ENABLED = asBool(
  readLocalStorageFlag("feature:appdata-spec-backfill-on-load") ?? import.meta.env.VITE_APPDATA_SPEC_BACKFILL_ON_LOAD,
  false,
);

export const WORKFLOW_ORCHESTRATION_PERSIST_ENABLED = asBool(
  readLocalStorageFlag("feature:workflow-orchestration-persist") ?? import.meta.env.VITE_WORKFLOW_ORCHESTRATION_PERSIST,
  true,
);

export const WORKFLOW_ORCHESTRATION_BOOT_PERSIST_DELAY_MS = asInt(
  readLocalStorageFlag("tuning:workflow-orchestration-boot-persist-delay-ms") ??
    import.meta.env.VITE_WORKFLOW_ORCHESTRATION_BOOT_PERSIST_DELAY_MS,
  60_000,
);

export const CHAT_REALTIME_PATCHING_ENABLED = asBool(
  readLocalStorageFlag("feature:chat-realtime-patching") ??
    import.meta.env.VITE_CHAT_REALTIME_PATCHING,
  true,
);

export const CHAT_MENTION_DEBOUNCE_MS = asInt(
  readLocalStorageFlag("tuning:chat-mention-debounce-ms") ??
    import.meta.env.VITE_CHAT_MENTION_DEBOUNCE_MS,
  300,
);
